import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  v2GetStartupOutreachActivityQueryKey,
  v2GetStartupOutreachOverviewQueryKey,
  v2GetStartupOutreachStatusQueryKey,
  v2GetStartupWalletQueryKey,
} from '@workspace/api-backend/react-query'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { useEffect } from 'react'
import { FounderLayout } from '../../../../components/founder-layout'
import { WorkspaceReadyGuard } from '../../../../components/workspace-ready-guard'
import type { ChannelId } from '../../../../features/outreach/components/channel-switcher'
import { OutreachActiveView } from '../../../../features/outreach/components/outreach-active-view'
import { OutreachComingSoon } from '../../../../features/outreach/components/outreach-coming-soon'
import { OutreachDisconnectingView } from '../../../../features/outreach/components/outreach-disconnecting-view'
import { useOutreachStatusQuery } from '../../../../features/outreach/hooks/use-outreach-status'
import { ShareProfileOutreachFrame } from '../../../../features/share-profile/components/share-profile-tabs'

/** Sidebar deep-link picking which campaign channel this page opens on. */
type OutreachTab = ChannelId

const DEFAULT_CHANNEL: ChannelId = 'investors'
const OUTREACH_TABS: OutreachTab[] = ['investors', 'portfolio']

type SearchParams = { topup?: 'ok' | 'canceled'; tab?: OutreachTab }

export const Route = createFileRoute('/app/startup/$id/outreach/')({
  component: GuardedOutreachPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const result: SearchParams = {}
    const t = search.topup
    if (t === 'ok' || t === 'canceled') result.topup = t
    const tab = search.tab
    if (OUTREACH_TABS.includes(tab as OutreachTab)) result.tab = tab as OutreachTab
    return result
  },
})

function GuardedOutreachPage() {
  return (
    <WorkspaceReadyGuard>
      <OutreachPage />
    </WorkspaceReadyGuard>
  )
}

function OutreachPage() {
  const { id: startupId } = Route.useParams()
  const { topup, tab: tabParam } = Route.useSearch()
  const hash = useRouterState({ select: (s) => s.location.hash })
  // Warm-intro is #warm-intro on this page. ?tab= still works for links already sent.
  const tab = tabParam ?? (hash === 'warm-intro' ? 'portfolio' : undefined)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // After Stripe round-trip, refresh wallet/status/overview/activity and strip
  // the query param so a refresh doesn't re-trigger this branch. `tab` is kept:
  // it drives which section the page scrolls to and must survive the replace.
  useEffect(() => {
    if (!topup) return
    if (topup === 'ok') {
      queryClient.invalidateQueries({
        queryKey: v2GetStartupOutreachStatusQueryKey({ path: { id: startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2GetStartupWalletQueryKey({ path: { id: startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2GetStartupOutreachOverviewQueryKey({ path: { id: startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2GetStartupOutreachActivityQueryKey({
          path: { id: startupId },
          query: { limit: 50 },
        }),
      })
    }
    navigate({
      to: '/app/startup/$id/outreach',
      params: { id: startupId },
      search: { tab },
      replace: true,
    })
  }, [topup, tab, startupId, queryClient, navigate])

  return (
    <FounderLayout activeNav="Share your Profile">
      <OutreachEntry startupId={startupId} tab={tab} />
    </FounderLayout>
  )
}

function OutreachEntry({ startupId, tab }: { startupId: string; tab?: OutreachTab }) {
  const statusQuery = useOutreachStatusQuery(startupId)

  // Every state sits in the same frame, so the header and card row do not jump
  // when the status resolves or when switching to Share Links.
  if (statusQuery.isLoading) {
    return (
      <ShareProfileOutreachFrame startupId={startupId}>
        <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      </ShareProfileOutreachFrame>
    )
  }

  const outreachStatus = statusQuery.data ?? null
  const status = outreachStatus?.status ?? null

  // Disconnect requested: campaign is paused and our team is removing the
  // founder's LinkedIn from our system. Block the dashboard entirely until
  // it's done (status flips to DISCONNECTED, which falls through to the dashboard).
  if (status === 'DISCONNECTING') {
    return (
      <ShareProfileOutreachFrame startupId={startupId}>
        <OutreachDisconnectingView />
      </ShareProfileOutreachFrame>
    )
  }

  // Anyone without a campaign lands on the acquisition page, and can start from
  // it. Home used to carry the pricing and trust content for the not-yet-
  // launched cohort; the wizard shows one focus card instead, so that content
  // needs a home and this is it.
  const hasProvisioned = status !== null
  if (!hasProvisioned) {
    return (
      <ShareProfileOutreachFrame startupId={startupId}>
        <OutreachComingSoon startupId={startupId} />
      </ShareProfileOutreachFrame>
    )
  }

  // Dashboard-first: every access state (including never-provisioned, where
  // status is null) lands on the dashboard. The dashboard gates the campaign
  // launch behind the four-step checklist instead of a separate setup wizard.
  //
  // `tab` is a sidebar deep-link, not a separate page: it picks the campaign
  // channel. It deliberately does not scroll — arriving from the sidebar should
  // land at the top of the page, not jump past the header.
  return (
    <ShareProfileOutreachFrame startupId={startupId} channel={tab ?? DEFAULT_CHANNEL}>
      <OutreachActiveView
        startupId={startupId}
        outreachStatus={outreachStatus}
        channel={tab ?? DEFAULT_CHANNEL}
      />
    </ShareProfileOutreachFrame>
  )
}
